import { formatTimestamp } from './handler';

export const SHIFT_8H_MS = 8 * 60 * 60 * 1000;

/** Same gradients as Attendance Time Tracking timeline */
export const TIMELINE_WORK_GRADIENT = {
  obs: 'linear-gradient(90deg, #a855f7 0%, #6366f1 100%)',
  hrx: 'linear-gradient(90deg, #0ea5e9 0%, #2563eb 100%)'
};

export const ms = (x) => {
  if (x === '' || x === null || x === undefined) return null;
  const n = Number(x);
  if (!Number.isFinite(n) || n <= 0) return null;
  // Unix seconds (~10 digits) vs milliseconds (~13 digits)
  return n < 1e12 ? n * 1000 : n;
};

/** Open/running interval — API may send end as "" or omit it. */
export const intervalEndMs = (end, now = Date.now()) => {
  const e = ms(end);
  return e && e > 0 ? e : now;
};

export const clamp01 = (n) => Math.max(0, Math.min(1, n));

export const fmtShort = (ts) => (ts ? formatTimestamp(ts, 'hh:mm A') : '—');

export const fmtShortLive = (ts, isLive, now = Date.now()) => {
  if (isLive) return `${formatTimestamp(now, 'hh:mm A')} (Now)`;
  return fmtShort(ts);
};

export const isHrxSessionActive = (hrxRow) => {
  if (!hrxRow) return false;
  const status = String(hrxRow.currentStatus || '').toLowerCase();
  return status.includes('checked in') || status.includes('on break');
};

export const isHrxCheckedOut = (hrxRow) => {
  if (!hrxRow) return false;
  const status = String(hrxRow.currentStatus || '').toLowerCase();
  if (status.includes('checked out')) return true;
  if (status.includes('checked in') || status.includes('on break')) return false;
  return Boolean(ms(hrxRow.checkOutTime) && !isHrxSessionActive(hrxRow));
};

export const getHrxDisplayCheckOutTime = (hrxRow) =>
  isHrxCheckedOut(hrxRow) ? (hrxRow?.checkOutTime ?? null) : null;

/** Map `/attendance-time-log` payload to dashboard chart shape for existing parsers. */
export const transformAttendanceTimeLogToChartData = (apiResponse) => {
  const days = Array.isArray(apiResponse?.data) ? apiResponse.data : [];
  const hrxTimeLogChart = [];
  const observantTimeLogChart = {};

  days.forEach((dayBlock) => {
    const dateKey = dayBlock?.date;
    if (!dateKey) return;

    (dayBlock?.users || []).forEach((user) => {
      (user?.hrx || []).forEach((row) => {
        hrxTimeLogChart.push(row);
      });

      const obsIntervals = (user?.observant || []).map((it) => ({
        assigneeId: it?.assigneeId ?? user?.userId,
        start: it?.start,
        end: it?.end
      }));

      if (!obsIntervals.length) return;

      if (!observantTimeLogChart[dateKey]) {
        observantTimeLogChart[dateKey] = { data: [] };
      }
      observantTimeLogChart[dateKey].data.push(...obsIntervals);
    });
  });

  return {
    data: { hrxTimeLogChart, observantTimeLogChart },
    pagination: apiResponse?.pagination
  };
};

export const collectEmployeesFromTimeLogData = (days) => {
  const byId = new Map();
  (Array.isArray(days) ? days : []).forEach((dayBlock) => {
    (dayBlock?.users || []).forEach((user) => {
      const id = user?.userId;
      if (!id || byId.has(id)) return;
      byId.set(id, {
        _id: id,
        name: String(user?.name || '').trim(),
        email: user?.email || ''
      });
    });
  });
  return Array.from(byId.values());
};

export const hasRunningObservantInterval = (intervals = []) =>
  (Array.isArray(intervals) ? intervals : []).some((it) => !ms(it?.end));

export const chartDataHasActiveSession = (chartData) => {
  const hrx = chartData?.data?.hrxTimeLogChart;
  if (Array.isArray(hrx) && hrx.some(isHrxSessionActive)) return true;
  const obs = chartData?.data?.observantTimeLogChart;
  if (!obs || typeof obs !== 'object') return false;
  return Object.values(obs).some((day) => hasRunningObservantInterval(day?.data));
};

export const durationToMs = (raw) => {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return 0;
  // At least 1 hour in ms — treat as already milliseconds (API / interval sums).
  if (n >= 3600000) return n;
  if (n < 10000) {
    if (n <= 24 * 60) return n * 60 * 1000;
    if (n <= 24 * 60 * 60) return n * 1000;
  }
  // Between 10k–3.6M: usually seconds (e.g. 10386 ≈ 2.9h), not ms.
  if (n <= 24 * 60 * 60) return n * 1000;
  return n;
};

export const sumIntervalsMs = (intervals = [], now = Date.now()) => {
  const list = Array.isArray(intervals) ? intervals : [];
  return list.reduce((acc, it) => {
    const s = ms(it?.start);
    const e = intervalEndMs(it?.end, now);
    if (!s || e <= s) return acc;
    return acc + (e - s);
  }, 0);
};

export const computeObservantIdleMs = (observantRow, now = Date.now()) => {
  if (!observantRow) return 0;
  const start = ms(observantRow?.startTime);
  const end = intervalEndMs(observantRow?.endTime, now);
  if (!start || !end || end <= start) return 0;
  const total = durationToMs(observantRow?.totalLog ?? 0);
  return Math.max(0, end - start - total);
};

const mergeIntervals = (intervals) => {
  const list = Array.isArray(intervals) ? intervals : [];
  const cleaned = list
    .map((it) => ({ start: ms(it?.start), end: ms(it?.end) }))
    .filter((it) => it.start && it.end && it.end > it.start)
    .sort((a, b) => a.start - b.start);
  if (!cleaned.length) return [];

  const merged = [cleaned[0]];
  for (let i = 1; i < cleaned.length; i += 1) {
    const cur = cleaned[i];
    const last = merged[merged.length - 1];
    if (cur.start <= last.end) {
      last.end = Math.max(last.end, cur.end);
    } else {
      merged.push(cur);
    }
  }
  return merged;
};

export const computeObservantIdleMsFromIntervals = (observantIntervals = [], now = Date.now()) => {
  const raw = Array.isArray(observantIntervals) ? observantIntervals : [];
  const normalized = raw
    .map((it) => ({ start: ms(it?.start), end: intervalEndMs(it?.end, now) }))
    .filter((it) => it.start && it.end && it.end > it.start)
    .sort((a, b) => a.start - b.start);
  if (!normalized.length) return 0;

  const merged = mergeIntervals(normalized);
  const spanStart = merged[0].start;
  const spanEnd = merged[merged.length - 1].end;
  const activeMs = merged.reduce((acc, it) => acc + (it.end - it.start), 0);
  return Math.max(0, spanEnd - spanStart - activeMs);
};

export const computeShiftRemainingMs = (workedMs, shiftMs = SHIFT_8H_MS) => {
  const worked = durationToMs(workedMs);
  const target = Number(shiftMs) || SHIFT_8H_MS;
  if (worked <= 0) return target;
  return Math.max(0, target - Math.min(worked, target));
};

export const computeWorkMsWithinWindow = (
  intervals = [],
  windowStartMs,
  windowEndMs,
  now = Date.now()
) => {
  const ws = ms(windowStartMs);
  const we = ms(windowEndMs);
  if (!ws || !we || we <= ws) return 0;

  const raw = Array.isArray(intervals) ? intervals : [];
  const normalized = raw
    .map((it) => ({ start: ms(it?.start), end: intervalEndMs(it?.end, now) }))
    .filter((it) => it.start && it.end && it.end > it.start)
    .map((it) => ({
      start: Math.max(ws, Math.min(we, it.start)),
      end: Math.max(ws, Math.min(we, it.end))
    }))
    .filter((it) => it.end > it.start)
    .sort((a, b) => a.start - b.start);

  if (!normalized.length) return 0;
  const merged = mergeIntervals(normalized);
  return merged.reduce((acc, it) => acc + (it.end - it.start), 0);
};

export const intervalsToTimelineWork = (intervals = [], now = Date.now()) => {
  const list = Array.isArray(intervals) ? intervals : [];
  return list
    .map((it) => {
      const startMs = ms(it?.start);
      const endMs = intervalEndMs(it?.end, now);
      if (!startMs || endMs <= startMs) return null;
      const isRunning = !ms(it?.end);
      return { startMs, endMs, isRunning };
    })
    .filter(Boolean)
    .sort((a, b) => a.startMs - b.startMs);
};

/** HRX check-in → breaks → check-out as work chunks (Attendance Time Tracking style). */
export const hrxRowToTimelineWork = (hrxRow, spanEndMs, now = Date.now()) => {
  const checkIn = ms(hrxRow?.checkInTime);
  if (!checkIn) return [];
  const endCap = Number(spanEndMs) || checkIn + SHIFT_8H_MS;
  const stillCheckedIn = isHrxSessionActive(hrxRow);
  const checkOut = stillCheckedIn
    ? Math.min(endCap, now)
    : ms(hrxRow?.checkOutTime) || Math.min(endCap, now);
  const sessions = pairBreaks(hrxRow?.lastBreakIn, hrxRow?.lastBreakOut)
    .map((b) => ({ breakIn: ms(b?.breakIn), breakOut: ms(b?.breakOut) }))
    .filter((b) => b.breakIn && b.breakOut && b.breakOut > b.breakIn)
    .sort((a, b) => a.breakIn - b.breakIn);

  const work = [];
  let cursor = checkIn;
  sessions.forEach((b) => {
    const bi = Math.max(checkIn, Math.min(endCap, b.breakIn));
    const bo = Math.max(checkIn, Math.min(endCap, b.breakOut));
    if (bi > cursor) work.push({ startMs: cursor, endMs: bi });
    cursor = Math.max(cursor, bo);
  });
  const end = Math.min(checkOut, endCap);
  if (end > cursor) work.push({ startMs: cursor, endMs: end });
  return work;
};

export const fallbackWorkFromDuration = (spanStartMs, workedMs, spanEndMs) => {
  const start = ms(spanStartMs);
  const end = ms(spanEndMs);
  if (!start || !end || end <= start) return [];
  const worked = Math.min(durationToMs(workedMs), end - start);
  if (worked <= 0) return [];
  return [{ startMs: start, endMs: start + worked }];
};

/**
 * Port of AttendanceTimeTracking `buildTimelineSegments` — work blocks + gap breaks on an 8h span.
 */
export const buildAttendanceTimelineSegments = (
  spanStartMs,
  spanEndMs,
  tasks = [],
  now = Date.now(),
  workColor = TIMELINE_WORK_GRADIENT.obs,
  options = {}
) => {
  const start = Number(spanStartMs);
  const end = Number(spanEndMs);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return [];

  const {
    includeEdgeBreaks = true, // backwards compatible (treated as both edges)
    includeLeadingBreaks = includeEdgeBreaks,
    includeTrailingBreaks = includeEdgeBreaks,
    minGapMs = 60 * 1000,
    minBreakWidthPct = 0.004
  } = options || {};
  const span = end - start;
  const src = Array.isArray(tasks) ? tasks : [];

  const work = src
    .map((t) => {
      const s = Number(t?.timelogs?.start);
      const e = Number(t?.timelogs?.end ?? t?.timelogs?.lastCheck ?? now);
      if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) return null;

      const clampedStart = Math.max(start, Math.min(end, s));
      const clampedEnd = Math.max(start, Math.min(end, e));
      if (clampedEnd <= clampedStart) return null;

      return {
        kind: 'work',
        startMs: clampedStart,
        endMs: clampedEnd,
        isRunning: !t?.timelogs?.end,
        color: t?.color || workColor
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.startMs - b.startMs);

  if (!work.length) return [];

  const segs = [];
  let cursor = includeLeadingBreaks ? start : work[0].startMs;

  for (let i = 0; i < work.length; i += 1) {
    const w = work[i];
    const gapStart = cursor;
    const gapEnd = Math.min(end, w.startMs);
    if (gapEnd - gapStart >= minGapMs) {
      segs.push({ kind: 'break', startMs: gapStart, endMs: gapEnd });
    }
    segs.push(w);
    cursor = Math.max(cursor, w.endMs);
  }

  if (includeTrailingBreaks && end - cursor >= minGapMs) {
    segs.push({ kind: 'break', startMs: cursor, endMs: end });
  }

  return segs
    .map((s) => {
      const left = (s.startMs - start) / span;
      let width = (s.endMs - s.startMs) / span;
      if (s.kind === 'break' && width > 0 && width < minBreakWidthPct) {
        width = minBreakWidthPct;
      }
      return {
        kind: s.kind,
        left: clamp01(left),
        width: clamp01(width),
        color: s.color,
        isRunning: s.isRunning,
        startMs: s.startMs,
        endMs: s.endMs
      };
    })
    .filter((s) => s.width > 0);
};

/** Per-variant span so Observant bars don't show empty space before first tracked interval. */
export const resolveVariantTimelineSpan = (variant, timeline, workIntervals, checkInTime) => {
  if (!timeline?.start || !timeline?.end) {
    return {
      start: timeline?.start ?? null,
      end: timeline?.end ?? null,
      shiftEndMs: timeline?.shiftEndMs ?? null
    };
  }

  const base = {
    start: timeline.start,
    end: timeline.end,
    shiftEndMs: timeline.shiftEndMs
  };

  if (variant !== 'obs') return base;

  const intervals = Array.isArray(workIntervals) ? workIntervals : [];
  if (!intervals.length) return base;

  const firstStart = intervals.reduce(
    (min, w) => (w?.startMs && (!min || w.startMs < min) ? w.startMs : min),
    null
  );
  if (!firstStart) return base;

  const checkIn = ms(checkInTime) || timeline.start;
  if (firstStart > checkIn) {
    return { ...base, start: firstStart };
  }

  return base;
};

/** Scale work/break segments into the left portion; reserve the right edge for pending blank. */
export const layoutShiftBarSegments = (baseSegs, pendingMs, shiftMs = SHIFT_8H_MS) => {
  const pendingFrac =
    pendingMs > 0 && shiftMs > 0 ? Math.min(1, Math.max(0, pendingMs / shiftMs)) : 0;
  const contentScale = 1 - pendingFrac;
  const scaled = (Array.isArray(baseSegs) ? baseSegs : []).map((s) => ({
    ...s,
    left: (s.left ?? 0) * contentScale,
    width: (s.width ?? 0) * contentScale
  }));
  if (pendingFrac <= 0.0001) return scaled;
  return [
    ...scaled,
    {
      kind: 'pending',
      left: contentScale,
      width: pendingFrac
    }
  ];
};

/** Snap first Observant work block to check-in so the bar has no leading blank gap. */
export const snapObsWorkToCheckIn = (workIntervals, checkInTime, spanStartMs) => {
  const sorted = [...(workIntervals || [])]
    .filter((w) => w?.startMs && w?.endMs && w.endMs > w.startMs)
    .sort((a, b) => a.startMs - b.startMs);
  if (!sorted.length) return sorted;

  const anchor = ms(checkInTime) || Number(spanStartMs);
  if (!Number.isFinite(anchor)) return sorted;

  const first = sorted[0];
  if (first.startMs <= anchor) return sorted;

  return [{ ...first, startMs: anchor }, ...sorted.slice(1)];
};

export const resolveTimelineWork = ({
  intervals,
  workedMs,
  spanStartMs,
  spanEndMs,
  hrxRow,
  variant,
  now = Date.now()
}) => {
  if (variant === 'hrx') {
    const fromRow = hrxRowToTimelineWork(hrxRow, spanEndMs, now);
    if (fromRow.length) return fromRow;
    return fallbackWorkFromDuration(spanStartMs, workedMs, spanEndMs);
  }
  const fromIntervals = intervalsToTimelineWork(intervals, now);
  if (fromIntervals.length) return fromIntervals;
  return fallbackWorkFromDuration(spanStartMs, workedMs, spanEndMs);
};

export const fmtHmsClock = (rawDuration) => {
  const msVal = durationToMs(rawDuration);
  if (!msVal) return '0:00:00';
  const totalSeconds = Math.floor(msVal / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
  const s = String(totalSeconds % 60).padStart(2, '0');
  return `${h}:${m}:${s}`;
};

/** Format a value that is already milliseconds (no heuristics). */
export const fmtHmsClockMs = (msVal) => {
  const n = Number(msVal);
  if (!Number.isFinite(n) || n <= 0) return '0:00:00';
  const totalSeconds = Math.floor(n / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
  const s = String(totalSeconds % 60).padStart(2, '0');
  return `${h}:${m}:${s}`;
};

export const pairBreaks = (breakIns, breakOuts) => {
  const ins = Array.isArray(breakIns) ? breakIns : [];
  const outs = Array.isArray(breakOuts) ? breakOuts : [];
  const len = Math.min(ins.length, outs.length);
  const sessions = [];
  for (let i = 0; i < len; i += 1) {
    const bi = ms(ins[i]);
    const bo = ms(outs[i]);
    if (bi && bo && bo > bi) sessions.push({ breakIn: bi, breakOut: bo });
  }
  return sessions;
};

export const computeHrxBreakMs = (hrxRow, now = Date.now()) => {
  if (!hrxRow) return 0;

  const ins = Array.isArray(hrxRow?.lastBreakIn)
    ? hrxRow.lastBreakIn
    : hrxRow?.lastBreakIn
      ? [hrxRow.lastBreakIn]
      : [];
  const outs = Array.isArray(hrxRow?.lastBreakOut)
    ? hrxRow.lastBreakOut
    : hrxRow?.lastBreakOut
      ? [hrxRow.lastBreakOut]
      : [];
  const closed = pairBreaks(ins, outs);

  let total = closed.reduce(
    (acc, b) => acc + Math.max(0, (ms(b.breakOut) || 0) - (ms(b.breakIn) || 0)),
    0
  );

  // If there is an unmatched break-in (user currently on break), count it up to now/check-out.
  if (ins.length > outs.length) {
    const lastIn = ms(ins[outs.length]);
    const end = isHrxSessionActive(hrxRow) ? now : ms(hrxRow?.checkOutTime) || now;
    if (lastIn && end > lastIn) total += end - lastIn;
  }

  // Prefer API-provided totalBreakMilliseconds only if it is larger (some backends aggregate it).
  const apiTotal = durationToMs(hrxRow?.totalBreakMilliseconds ?? 0);
  if (apiTotal > total) total = apiTotal;

  return total;
};

const clampToSpan = (value, start, end) => Math.max(start, Math.min(end, value));

/**
 * Day view: bar is at least 8h from check-in; extends when work/check-out goes past 8h.
 */
export const buildDayTimelineSpan = ({
  checkInTime,
  checkOutTime,
  observantIntervals = [],
  observantMilliseconds = 0,
  observantEndTime,
  hrxRow,
  hrxMilliseconds = 0,
  now = Date.now()
}) => {
  const start = ms(checkInTime);
  if (!start) return null;

  const shiftEnd = start + SHIFT_8H_MS;
  const end = shiftEnd;

  // Overtime is computed from actual activity/end times, but the rendered span stays fixed at 8h.
  let actualEnd = shiftEnd;
  const bumpActual = (ts) => {
    const e = ms(ts);
    if (e && e > actualEnd) actualEnd = e;
  };
  bumpActual(checkOutTime);
  bumpActual(observantEndTime);
  bumpActual(hrxRow?.checkOutTime);
  observantIntervals.forEach((it) => bumpActual(intervalEndMs(it?.end, now)));

  const obsMs = observantIntervals.length
    ? sumIntervalsMs(observantIntervals, now)
    : durationToMs(observantMilliseconds);
  const hrxMs = Number(hrxMilliseconds || 0);
  if (obsMs > SHIFT_8H_MS) actualEnd = Math.max(actualEnd, start + obsMs);
  if (hrxMs > SHIFT_8H_MS) actualEnd = Math.max(actualEnd, start + hrxMs);

  return {
    start,
    end,
    span: end - start,
    shiftEndMs: shiftEnd,
    isOvertime: actualEnd > shiftEnd,
    actualEndMs: actualEnd
  };
};

export const buildTimeline = ({ checkInTime, checkOutTime, breakSessions, fixedSpanMs }) => {
  const start = ms(checkInTime);
  const fixed = Number(fixedSpanMs);
  const end =
    start && Number.isFinite(fixed) && fixed > 0 ? start + fixed : ms(checkOutTime) || Date.now();
  if (!start || end <= start) return null;

  const breaks = (breakSessions || [])
    .map((b) => ({ in: ms(b?.breakIn), out: ms(b?.breakOut) }))
    .filter((b) => b.in && b.out && b.out > b.in)
    .sort((a, b) => a.in - b.in);

  const span = end - start;
  const segments = [];
  let cursor = start;
  breaks.forEach((b) => {
    const bi = Math.max(start, Math.min(end, b.in));
    const bo = Math.max(start, Math.min(end, b.out));
    if (bi > cursor) segments.push({ type: 'work', start: cursor, end: bi });
    if (bo > bi) segments.push({ type: 'break', start: bi, end: bo });
    cursor = Math.max(cursor, bo);
  });
  if (end > cursor) segments.push({ type: 'work', start: cursor, end });

  const withPos = segments.map((s) => {
    const left = clamp01((s.start - start) / span);
    const right = clamp01((s.end - start) / span);
    return { ...s, left, width: Math.max(0, right - left) };
  });

  return { start, end, span, segments: withPos };
};

/**
 * HRX segments for overview bar (explicit breaks from lastBreakIn/Out).
 * Ensures break is rendered exactly between check-in and check-out.
 */
export const buildHrxOverviewSegments = (
  spanStartMs,
  spanEndMs,
  hrxRow,
  now = Date.now(),
  workColor = TIMELINE_WORK_GRADIENT.hrx,
  options = {}
) => {
  const start = Number(spanStartMs);
  const end = Number(spanEndMs);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start || !hrxRow) return [];

  const { minGapMs = 60 * 1000, minBreakWidthPct = 0.004 } = options || {};
  const span = end - start;

  const checkIn = ms(hrxRow?.checkInTime);
  if (!checkIn) return [];

  const checkedInAt = clampToSpan(checkIn, start, end);
  const stillCheckedIn = isHrxSessionActive(hrxRow);
  const rawOut = ms(hrxRow?.checkOutTime);
  const checkedOutAt = clampToSpan(stillCheckedIn ? now : rawOut || now, start, end);
  if (checkedOutAt <= checkedInAt) return [];

  const breakIns = Array.isArray(hrxRow?.lastBreakIn)
    ? hrxRow.lastBreakIn
    : hrxRow?.lastBreakIn
      ? [hrxRow.lastBreakIn]
      : [];
  const breakOuts = Array.isArray(hrxRow?.lastBreakOut)
    ? hrxRow.lastBreakOut
    : hrxRow?.lastBreakOut
      ? [hrxRow.lastBreakOut]
      : [];

  const closedBreaks = pairBreaks(breakIns, breakOuts)
    .map((b) => ({
      in: clampToSpan(ms(b.breakIn), checkedInAt, checkedOutAt),
      out: clampToSpan(ms(b.breakOut), checkedInAt, checkedOutAt)
    }))
    .filter((b) => b.in && b.out && b.out > b.in);

  const openBreak =
    breakIns.length > breakOuts.length
      ? (() => {
          const bi = ms(breakIns[breakOuts.length]);
          if (!bi) return null;
          const bo = stillCheckedIn ? now : rawOut || now;
          const inClamped = clampToSpan(bi, checkedInAt, checkedOutAt);
          const outClamped = clampToSpan(bo, checkedInAt, checkedOutAt);
          return outClamped > inClamped ? { in: inClamped, out: outClamped } : null;
        })()
      : null;

  const breaks = [...closedBreaks, ...(openBreak ? [openBreak] : [])].sort((a, b) => a.in - b.in);

  const segs = [];
  let cursor = checkedInAt;

  breaks.forEach((b) => {
    if (b.in - cursor >= minGapMs) {
      segs.push({ kind: 'work', startMs: cursor, endMs: b.in, color: workColor });
    }
    if (b.out - b.in >= minGapMs) {
      segs.push({ kind: 'break', startMs: b.in, endMs: b.out });
    }
    cursor = Math.max(cursor, b.out);
  });

  if (checkedOutAt - cursor >= minGapMs) {
    segs.push({ kind: 'work', startMs: cursor, endMs: checkedOutAt, color: workColor });
  }

  return segs
    .map((s) => {
      const left = (s.startMs - start) / span;
      let width = (s.endMs - s.startMs) / span;
      if (s.kind === 'break' && width > 0 && width < minBreakWidthPct) width = minBreakWidthPct;
      return {
        kind: s.kind,
        left: clamp01(left),
        width: clamp01(width),
        color: s.color,
        startMs: s.startMs,
        endMs: s.endMs
      };
    })
    .filter((s) => s.width > 0);
};

export const buildActiveFillSegments = (timeline, activeMilliseconds) => {
  if (!timeline) return null;
  const totalActive = Math.max(
    0,
    Math.min(Number(activeMilliseconds || 0), Number(timeline.span || 0))
  );
  const workSegments = timeline.segments.filter((s) => s.type === 'work');
  const breakSegments = timeline.segments.filter((s) => s.type === 'break');
  const filled = [];
  let remaining = totalActive;

  workSegments.forEach((s) => {
    const segMs = s.end - s.start;
    if (segMs <= 0) return;
    const take = Math.max(0, Math.min(segMs, remaining));
    if (take > 0) {
      const end = s.start + take;
      const span = timeline.end - timeline.start;
      const left = clamp01((s.start - timeline.start) / span);
      const right = clamp01((end - timeline.start) / span);
      filled.push({ type: 'active', start: s.start, end, left, width: Math.max(0, right - left) });
      remaining -= take;
    }
  });

  return { filled, breaks: breakSegments.map((b) => ({ ...b, type: 'break' })) };
};

export const buildSegmentsFromIntervals = (timeline, intervals) => {
  if (!timeline) return null;
  const baseStart = ms(timeline.start);
  const baseEnd = ms(timeline.end);
  if (!baseStart || !baseEnd || baseEnd <= baseStart) return null;

  const raw = Array.isArray(intervals) ? intervals : [];
  const cleaned = raw
    .map((it) => ({ start: ms(it?.start), end: ms(it?.end) }))
    .filter((it) => it.start && it.end && it.end > it.start)
    .sort((a, b) => a.start - b.start);

  if (!cleaned.length) return { filled: [], breaks: [] };

  const merged = [];
  cleaned.forEach((it) => {
    const start = Math.max(baseStart, Math.min(baseEnd, it.start));
    const end = Math.max(baseStart, Math.min(baseEnd, it.end));
    if (end <= start) return;
    const last = merged[merged.length - 1];
    if (!last) {
      merged.push({ start, end });
      return;
    }
    if (start <= last.end) {
      last.end = Math.max(last.end, end);
      return;
    }
    merged.push({ start, end });
  });

  const span = baseEnd - baseStart;
  const filled = merged.map((s) => {
    const left = clamp01((s.start - baseStart) / span);
    const right = clamp01((s.end - baseStart) / span);
    return { type: 'active', start: s.start, end: s.end, left, width: Math.max(0, right - left) };
  });

  const breaks = [];
  for (let i = 0; i < merged.length - 1; i += 1) {
    const a = merged[i];
    const b = merged[i + 1];
    if (b.start > a.end) {
      const left = clamp01((a.end - baseStart) / span);
      const right = clamp01((b.start - baseStart) / span);
      breaks.push({
        type: 'break',
        start: a.end,
        end: b.start,
        left,
        width: Math.max(0, right - left)
      });
    }
  }

  return { filled, breaks };
};

export const getObservantKeysInRange = (obsObj, fromMoment, toMoment) => {
  if (!obsObj || typeof obsObj !== 'object') return [];
  const start = fromMoment.clone().startOf('day');
  const end = toMoment.clone().startOf('day');
  const keys = [];
  const cur = start.clone();
  while (cur.isSameOrBefore(end, 'day')) {
    keys.push(cur.format('DD-MMM-YYYY'));
    cur.add(1, 'day');
  }
  return keys.filter((k) => obsObj[k]);
};

export const aggregateObservantForRange = (obsObj, fromMoment, toMoment) => {
  const keys = getObservantKeysInRange(obsObj, fromMoment, toMoment);
  let totalLog = 0;
  const intervals = [];
  let startTime = null;
  let endTime = null;

  keys.forEach((key) => {
    const row = obsObj[key];
    if (!row) return;
    totalLog += durationToMs(row.totalLog);
    if (Array.isArray(row.data)) intervals.push(...row.data);
    const s = ms(row.startTime);
    const e = ms(row.endTime);
    if (s && (!startTime || s < startTime)) startTime = s;
    if (e && (!endTime || e > endTime)) endTime = e;
  });

  return { totalLog, data: intervals, startTime, endTime };
};

const getObservantKeysForPeriod = (obsObj, periodType, fromMoment, toMoment) => {
  if (periodType === 'Day') {
    return [fromMoment.clone().utc().format('DD-MMM-YYYY')];
  }
  return getObservantKeysInRange(obsObj, fromMoment, toMoment);
};

export const getHrxRowForUser = (hrxChart, userId, periodType, fromMoment, toMoment) => {
  let rows = (Array.isArray(hrxChart) ? hrxChart : []).filter(
    (r) => String(r?.userId) === String(userId)
  );
  if (!rows.length) return null;

  if (periodType !== 'Day' && fromMoment && toMoment) {
    const dayNums = new Set(
      buildDayMomentsInRange(fromMoment, toMoment).map((d) =>
        Number(d.clone().utc().format('YYYYMMDD'))
      )
    );
    rows = rows.filter((r) => dayNums.has(Number(r?.date)));
    if (!rows.length) return null;
  }

  if (periodType === 'Day') {
    if (fromMoment) {
      const yyyymmdd = Number(fromMoment.clone().utc().format('YYYYMMDD'));
      return rows.find((r) => Number(r?.date) === yyyymmdd) || rows[0];
    }
    return rows[0];
  }

  let checkInTime = null;
  let checkOutTime = null;
  let totalWorkMilliseconds = 0;
  let totalBreakMilliseconds = 0;
  const lastBreakIn = [];
  const lastBreakOut = [];

  rows.forEach((r) => {
    const ci = ms(r.checkInTime);
    const co = ms(r.checkOutTime);
    if (ci && (!checkInTime || ci < checkInTime)) checkInTime = ci;
    if (co && (!checkOutTime || co > checkOutTime)) checkOutTime = co;
    totalWorkMilliseconds += durationToMs(r.totalWorkMilliseconds ?? r.totalWorkedMilliseconds);
    totalBreakMilliseconds += durationToMs(r.totalBreakMilliseconds);
    if (Array.isArray(r.lastBreakIn)) lastBreakIn.push(...r.lastBreakIn);
    if (Array.isArray(r.lastBreakOut)) lastBreakOut.push(...r.lastBreakOut);
  });

  return {
    ...rows[0],
    checkInTime,
    checkOutTime,
    totalWorkMilliseconds,
    totalBreakMilliseconds,
    lastBreakIn,
    lastBreakOut
  };
};

export const buildObservantRowForUser = (
  obsObj,
  userId,
  periodType,
  fromMoment,
  toMoment,
  now = Date.now()
) => {
  const keys = getObservantKeysForPeriod(obsObj, periodType, fromMoment, toMoment);
  const data = [];
  let startTime = null;
  let endTime = null;
  let totalLog = 0;

  keys.forEach((key) => {
    const dayRow = obsObj?.[key];
    if (!dayRow) return;
    const allIntervals = Array.isArray(dayRow.data) ? dayRow.data : [];
    const hasAssigneeIds = allIntervals.some((it) => String(it?.assigneeId || '').trim());
    const intervals = hasAssigneeIds
      ? allIntervals.filter((it) => {
          const a = String(it?.assigneeId || '').trim();
          // Some APIs send empty assigneeId even for the current user.
          return a === '' || a === String(userId);
        })
      : allIntervals;
    intervals.forEach((it) => {
      const s = ms(it.start);
      const e = intervalEndMs(it.end, now);
      if (!s || e <= s) return;
      data.push({ ...it, end: ms(it.end) ? it.end : e });
      totalLog += e - s;
      if (!startTime || s < startTime) startTime = s;
      if (!endTime || e > endTime) endTime = e;
    });
  });

  if (!data.length && totalLog <= 0) return null;
  return { totalLog, data, startTime, endTime };
};

const toYmd = (m) => m.clone().utc().format('YYYY-MM-DD');

export const buildDayMomentsInRange = (fromMoment, toMoment) => {
  const start = fromMoment.clone().utc().startOf('day');
  const end = toMoment.clone().utc().startOf('day');
  const out = [];
  const cur = start.clone();
  while (cur.isSameOrBefore(end, 'day')) {
    out.push(cur.clone());
    cur.add(1, 'day');
  }
  return out;
};

export const getHrxRowForUserOnDay = (hrxChart, userId, dayMoment) => {
  const yyyymmdd = Number(dayMoment.clone().utc().format('YYYYMMDD'));
  const rows = Array.isArray(hrxChart) ? hrxChart : [];
  return (
    rows.find((r) => String(r?.userId) === String(userId) && Number(r?.date) === yyyymmdd) || null
  );
};

export const buildObservantRowForUserOnDay = (obsObj, userId, dayMoment, now = Date.now()) => {
  const key = dayMoment.clone().utc().format('DD-MMM-YYYY');
  const dayRow = obsObj?.[key];
  if (!dayRow) return null;
  const allIntervals = Array.isArray(dayRow.data) ? dayRow.data : [];
  const hasAssigneeIds = allIntervals.some((it) => String(it?.assigneeId || '').trim());
  const intervals = hasAssigneeIds
    ? allIntervals.filter((it) => {
        const a = String(it?.assigneeId || '').trim();
        // Some APIs send empty assigneeId even for the current user.
        return a === '' || a === String(userId);
      })
    : allIntervals;
  if (!intervals.length) return null;

  let computedLog = 0;
  let startTime = ms(dayRow.startTime);
  let endTime = ms(dayRow.endTime);
  const data = [];

  intervals.forEach((it) => {
    const s = ms(it.start);
    const e = intervalEndMs(it.end, now);
    if (!s || e <= s) return;
    data.push({ ...it, end: ms(it.end) ? it.end : e });
    computedLog += e - s;
    if (!startTime || s < startTime) startTime = s;
    if (!endTime || e > endTime) endTime = e;
  });

  if (!data.length) return null;

  const apiTotal = Number(dayRow.totalLog);
  const totalLog = hasAssigneeIds
    ? computedLog
    : Number.isFinite(apiTotal) && apiTotal > 0
      ? apiTotal
      : computedLog;

  return { totalLog, data, startTime, endTime };
};

/** Sum HRX work ms per day (matches daily row totals); avoids week-span window inflation. */
export const sumHrxMillisecondsForRange = (
  hrxChart,
  userId,
  fromMoment,
  toMoment,
  now = Date.now()
) => {
  if (!hrxChart || !userId || !fromMoment || !toMoment) return 0;
  return buildDayMomentsInRange(fromMoment, toMoment).reduce((acc, d) => {
    const hrxRow = getHrxRowForUserOnDay(hrxChart, userId, d);
    if (!hrxRow) return acc;
    const { hrxMilliseconds } = parseChartSlices(
      hrxRow,
      null,
      'Day',
      d.clone().startOf('day'),
      d.clone().endOf('day'),
      now
    );
    return acc + Number(hrxMilliseconds || 0);
  }, 0);
};

export const buildDailyBreakdownForUser = (
  chartData,
  userId,
  fromMoment,
  toMoment,
  now = Date.now()
) => {
  const hrxChart = chartData?.data?.hrxTimeLogChart;
  const obsObj = chartData?.data?.observantTimeLogChart ?? {};
  const days = buildDayMomentsInRange(fromMoment, toMoment);
  return days.map((d) => {
    const hrxRow = getHrxRowForUserOnDay(hrxChart, userId, d);
    const obsRow = buildObservantRowForUserOnDay(obsObj, userId, d, now);
    const slices = parseChartSlices(
      hrxRow,
      obsRow,
      'Day',
      d.clone().startOf('day'),
      d.clone().endOf('day'),
      now
    );
    return {
      ymd: toYmd(d),
      label: d.format('ddd, DD MMM'),
      ...slices
    };
  });
};

export const parseChartSlices = (
  hrxRow,
  observantRow,
  periodType,
  fromMoment,
  toMoment,
  now = Date.now()
) => {
  const observantIntervals = Array.isArray(observantRow?.data) ? observantRow.data : [];
  const firstInterval = observantIntervals[0];
  const checkInTime =
    hrxRow?.checkInTime ?? observantRow?.startTime ?? firstInterval?.start ?? null;
  const isHrxActive = isHrxSessionActive(hrxRow);
  const isObsRunning = hasRunningObservantInterval(observantIntervals);
  const isSessionActive = isHrxActive || isObsRunning;

  const checkOutTime =
    hrxRow?.checkOutTime ??
    observantRow?.endTime ??
    (firstInterval?.start ? intervalEndMs(firstInterval?.end, now) : null);
  const displayCheckOutTime = isSessionActive ? now : checkOutTime;

  const observantMilliseconds = observantIntervals.length
    ? sumIntervalsMs(observantIntervals, now)
    : durationToMs(observantRow?.totalLog ?? 0);
  // Observant break: idle gaps between tracking intervals only (not before first log / after last).
  const checkInMs = ms(checkInTime);
  const checkOutMs = ms(displayCheckOutTime);
  const windowEndMs =
    checkInMs && checkOutMs ? Math.min(checkOutMs, checkInMs + SHIFT_8H_MS) : null;
  const observantBreakMilliseconds = observantIntervals.length
    ? computeObservantIdleMsFromIntervals(observantIntervals, now)
    : periodType === 'Day' && checkInMs && windowEndMs && windowEndMs > checkInMs
      ? Math.max(
          0,
          windowEndMs -
            checkInMs -
            computeWorkMsWithinWindow(observantIntervals, checkInMs, windowEndMs, now)
        )
      : computeObservantIdleMs(observantRow, now);
  const breakSessions = pairBreaks(hrxRow?.lastBreakIn, hrxRow?.lastBreakOut);

  let timeline;
  if (periodType === 'Day') {
    const daySpan = buildDayTimelineSpan({
      checkInTime,
      checkOutTime: isSessionActive ? now : checkOutTime,
      observantIntervals,
      observantMilliseconds,
      observantEndTime: isObsRunning ? now : observantRow?.endTime,
      hrxRow,
      hrxMilliseconds: 0,
      now
    });
    timeline = daySpan
      ? {
          start: daySpan.start,
          end: daySpan.end,
          span: daySpan.span,
          shiftEndMs: daySpan.shiftEndMs,
          isOvertime: daySpan.isOvertime,
          segments: []
        }
      : null;
  } else {
    timeline = buildTimeline({
      checkInTime,
      checkOutTime,
      breakSessions,
      fixedSpanMs: null
    });
  }

  const spanEndMs = timeline?.end ?? null;
  const obsWorkIntervals = resolveTimelineWork({
    intervals: observantIntervals,
    workedMs: observantMilliseconds,
    spanStartMs: timeline?.start,
    spanEndMs,
    variant: 'obs',
    now
  });
  const hrxWorkIntervals = resolveTimelineWork({
    intervals: [],
    workedMs: 0,
    spanStartMs: timeline?.start,
    spanEndMs,
    hrxRow,
    variant: 'hrx',
    now
  });

  const hrxWindowWorkMs = hrxWorkIntervals.length
    ? hrxWorkIntervals.reduce((acc, w) => acc + Math.max(0, w.endMs - w.startMs), 0)
    : 0;
  // API totalWorkMilliseconds is already "work only" (break excluded).
  const hrxApiWorkMs = durationToMs(
    hrxRow?.totalWorkMilliseconds ?? hrxRow?.totalWorkedMilliseconds ?? 0
  );
  // Day: max(window, api). Week/Month: merged row spans multiple days — use API sum only here;
  // accordion totals override via sumHrxMillisecondsForRange in parseTeamChartForEmployees.
  const hrxMilliseconds =
    periodType === 'Day' ? Math.max(hrxWindowWorkMs, hrxApiWorkMs) : hrxApiWorkMs;

  const hasObs = observantIntervals.length > 0 || observantMilliseconds > 0;
  const hasHrx = Boolean(ms(hrxRow?.checkInTime) || hrxMilliseconds > 0);
  const totalBreakMilliseconds = computeHrxBreakMs(hrxRow, now);

  return {
    hrxRow,
    observantRow,
    timeline,
    observantIntervals,
    obsWorkIntervals,
    hrxWorkIntervals,
    breakSessions,
    totalBreakMilliseconds,
    observantBreakMilliseconds,
    checkInTime,
    checkOutTime,
    displayCheckOutTime,
    isSessionActive,
    isHrxActive,
    isObsRunning,
    hrxMilliseconds,
    observantMilliseconds,
    hasObs,
    hasHrx
  };
};

export const parseChartForEmployee = (chartData, periodType, fromMoment, toMoment) => {
  const hrxChart = chartData?.data?.hrxTimeLogChart;
  const obsObj = chartData?.data?.observantTimeLogChart ?? {};
  const userId = Array.isArray(hrxChart) && hrxChart[0]?.userId ? hrxChart[0].userId : null;

  let observantRow = null;
  if (periodType === 'Day') {
    const key = fromMoment.clone().utc().format('DD-MMM-YYYY');
    observantRow = obsObj[key] || null;
    if (userId && observantRow) {
      observantRow = buildObservantRowForUser(obsObj, userId, periodType, fromMoment, toMoment);
    }
  } else {
    observantRow = userId
      ? buildObservantRowForUser(obsObj, userId, periodType, fromMoment, toMoment)
      : aggregateObservantForRange(obsObj, fromMoment, toMoment);
  }

  const hrxRow = Array.isArray(hrxChart) && hrxChart.length ? hrxChart[0] : null;
  return parseChartSlices(hrxRow, observantRow, periodType, fromMoment, toMoment, Date.now());
};

export const parseTeamChartForEmployees = (
  chartData,
  employees,
  periodType,
  fromMoment,
  toMoment,
  now = Date.now()
) => {
  const hrxChart = chartData?.data?.hrxTimeLogChart;
  const obsObj = chartData?.data?.observantTimeLogChart ?? {};
  const list = Array.isArray(employees) ? employees : [];

  return list.map((emp) => {
    const userId = emp?._id;
    const hrxRow = userId
      ? getHrxRowForUser(hrxChart, userId, periodType, fromMoment, toMoment)
      : null;
    const observantRow = userId
      ? buildObservantRowForUser(obsObj, userId, periodType, fromMoment, toMoment, now)
      : null;

    const slices = parseChartSlices(hrxRow, observantRow, periodType, fromMoment, toMoment, now);
    const hrxMilliseconds =
      periodType !== 'Day' && userId
        ? sumHrxMillisecondsForRange(hrxChart, userId, fromMoment, toMoment, now)
        : slices.hrxMilliseconds;

    return {
      id: userId,
      employee: emp,
      ...slices,
      hrxMilliseconds,
      hasHrx: periodType !== 'Day' ? hrxMilliseconds > 0 : slices.hasHrx
    };
  });
};

export async function mapWithConcurrency(items, limit, fn) {
  const results = new Array(items.length);
  let index = 0;

  const workers = Array.from({ length: Math.min(limit, items.length || 1) }, async () => {
    while (index < items.length) {
      const current = index;
      index += 1;
      results[current] = await fn(items[current], current);
    }
  });

  await Promise.all(workers);
  return results;
}
